export interface StoredUser {
  id: string;
  email: string;
  name?: string;
}

export interface AuthSession {
  accessToken: string;
  refreshToken: string | null;
  user: StoredUser | null;
  rememberMe: boolean;
}

const REMEMBER_KEY = 'fe_ai_remember_me';
const ACCESS_TOKEN_KEY = 'fe_ai_access_token';
const REFRESH_TOKEN_KEY = 'fe_ai_refresh_token';
const USER_KEY = 'fe_ai_user';

const TOKEN_KEYS = [ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY] as const;

export function getRememberMePreference(): boolean {
  try {
    const stored = localStorage.getItem(REMEMBER_KEY);
    if (stored === '0') return false;
    if (stored === '1') return true;
  } catch (_) {}
  return true;
}

export function setRememberMePreference(rememberMe: boolean): void {
  try {
    localStorage.setItem(REMEMBER_KEY, rememberMe ? '1' : '0');
  } catch (_) {}
}

function storageForRememberMe(rememberMe: boolean): Storage {
  return rememberMe ? localStorage : sessionStorage;
}

function activeStorage(): Storage | null {
  try {
    if (sessionStorage.getItem(ACCESS_TOKEN_KEY)) return sessionStorage;
    if (localStorage.getItem(ACCESS_TOKEN_KEY)) return localStorage;
  } catch (_) {}
  return null;
}

export function clearAuthSession(): void {
  try {
    for (const key of TOKEN_KEYS) {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    }
  } catch (_) {}
}

export function saveAuthSession(session: {
  accessToken: string;
  refreshToken?: string | null;
  user?: StoredUser | null;
  rememberMe: boolean;
}): void {
  clearAuthSession();
  setRememberMePreference(session.rememberMe);
  const storage = storageForRememberMe(session.rememberMe);
  try {
    storage.setItem(ACCESS_TOKEN_KEY, session.accessToken);
    if (session.refreshToken) {
      storage.setItem(REFRESH_TOKEN_KEY, session.refreshToken);
    }
    if (session.user) {
      storage.setItem(USER_KEY, JSON.stringify(session.user));
    }
  } catch (_) {}
}

export function loadAuthSession(): AuthSession | null {
  const storage = activeStorage();
  if (!storage) return null;
  try {
    const accessToken = storage.getItem(ACCESS_TOKEN_KEY);
    if (!accessToken) return null;
    const refreshToken = storage.getItem(REFRESH_TOKEN_KEY);
    const userRaw = storage.getItem(USER_KEY);
    let user: StoredUser | null = null;
    if (userRaw) {
      try {
        user = JSON.parse(userRaw) as StoredUser;
      } catch (_) {
        user = null;
      }
    }
    return {
      accessToken,
      refreshToken,
      user,
      rememberMe: storage === localStorage,
    };
  } catch (_) {
    return null;
  }
}

export function updateStoredAccessToken(accessToken: string, refreshToken?: string | null): void {
  const storage = activeStorage();
  if (!storage) return;
  try {
    storage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken) {
      storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  } catch (_) {}
}
